import ical from 'ical-generator';
import sanitizeHtml, { Attributes } from 'sanitize-html';

import type { EventDetails } from './types';

const toISOQueryString = (time: string | Date) =>
  new Date(time).toISOString().replace(/[-:]|\.\d{3}/g, '');

const concatUrl = (
  acc: string,
  [key, value]: [key: string, value: string],
  index: number,
): string =>
  value
    ? `${acc}${index === 0 ? '?' : '&'}${key}=${encodeURIComponent(value)}`
    : acc;

const createUrl = (initial: string, query: Record<string, string>): string =>
  Object.entries(query).reduce(concatUrl, initial);

const createGoogleCalendarUrl = ({
  title,
  description,
  location,
  startTime,
  endTime,
}: Omit<EventDetails, 'url'>): string =>
  createUrl('https://calendar.google.com/calendar/render', {
    action: 'TEMPLATE',
    dates: `${toISOQueryString(startTime)}/${toISOQueryString(endTime)}`,
    location: location,
    details: description.replaceAll('\\n', '<br>'),
    text: title,
  });

const createICSCalendarUrl = ({
  title,
  description,
  startTime,
  endTime,
  location,
  url,
}: EventDetails): string => {
  const calendar = ical();
  calendar.prodId({
    company: 'RingCentral',
    product: 'Events',
  });
  calendar.createEvent({
    start: startTime,
    end: endTime,
    allDay: false,
    location: encodeURIComponent(location),
    description: {
      plain: descriptionToText(description),
      html: description,
    },
    url: `${encodeURIComponent(
      url ??
        (typeof document === 'undefined' ? '' : document.URL.split('#')[0]),
    )}`,
    summary: `${encodeURIComponent(title)}`,
  });

  return `data:text/calendar;charset=utf8,${calendar.toString()}`;
};

function descriptionToText(description: string) {
  let sanitized = sanitizeHtml(description, {
    allowedTags: ['p', 'a', 'li'],
    allowedAttributes: {},
    transformTags: {
      /**
       * Converts all anchor tags to paragraph tags, preserving the href attribute, which
       * eventually will be replaced by escape character.
       *
       * Ideally we would like to preserve the anchor text as well, but that is not possible
       * because sanitize-html does not support this.
       *
       * A feature request for the above is open here: https://github.com/apostrophecms/sanitize-html/issues/703
       * Ideally, we would output "`${text} <${attribs.href}>`", similar to what Outlook does for links.
       */
      a: function (_tagName: string, attribs: Attributes) {
        return {
          tagName: 'p',
          attribs: {},
          text: attribs.href,
        };
      },
    },
  });

  // replace <p> and <li> tags with newlines because Apple Calendar does not support them
  sanitized = sanitized.replace(/<\/?p>/g, '\n');
  sanitized = sanitized.replace(/<\/?li>/g, '\n');

  return sanitized;
}

export { createGoogleCalendarUrl, createICSCalendarUrl, toISOQueryString };
